/**
 * Builder editor plugin registry.
 *
 * Mirrors the reactive-json `plugins` pattern: every editor (text, link,
 * image-drupal, …) registers itself once. Every consumer (top-level modal,
 * inline contentEditable activation, sub-modal inside a CollectionEditor,
 * future inline-cell renderers, …) looks the editor up by `type`.
 *
 * Plugin shape:
 *   {
 *     type:        string,               // identifier — must match YAML `type:` (or legacy `editorType:`)
 *     mode:        'inline' | 'modal',   // how the cell renderer surfaces it inside a CollectionEditor
 *     surface?:    'fullscreen',         // opt out of the anchored popover chrome: the editor
 *                                        // renders its own dialog in a portal (médiathèque)
 *                                        // - 'inline' cells render <Cell value onChange .../> directly in the row
 *                                        // - 'modal'  cells render <Cell value onClick .../> as a preview that opens <Editor> in a sub-modal
 *     Editor:      ReactComponent,       // the modal/sub-modal form
 *                                        //   props: { value, label, onSave: (v) => void, onClose: () => void, ...config }
 *     Cell?:       ReactComponent,       // preview / inline-input used by CollectionEditor
 *                                        //   mode 'inline':  { value, onChange: (v) => void, placeholder, ...config }
 *                                        //   mode 'modal':   { value, onClick: () => void, ...config }
 *                                        // Optional — a default fallback is rendered when omitted.
 *     defaultValue?: any,                // optional seed for a new row in a list of this type
 *     isContentful?: boolean,            // collection editor uses this to decide whether the cell counts
 *                                        // toward "row is empty" — defaults to (mode === 'inline')
 *   }
 *
 * The same plugin powers all three surfaces: a project can register a custom
 * editor type (e.g. `color`, `iconfont`, `gltf-model`) and immediately use it
 * both at the top level (`fields: [{type: color}]`) and inside any list
 * (`columns: [{type: color}]`).
 */

const EDITORS = new Map()

export const registerEditor = (type, plugin) => {
  if (!type || typeof type !== 'string') {
    console.warn('[builder] registerEditor: invalid type', type)
    return
  }
  // Un type rend SOIT une pop-in (`Editor`), SOIT un contrôle posé dans le
  // formulaire (`mode: 'inline'` + `Cell`). Exiger `Editor` de tous refusait
  // silencieusement le second cas — le champ s'affichait alors « Type
  // d'éditeur inconnu », alors que sa cellule était bien là.
  if (!plugin || typeof plugin !== 'object') {
    console.warn(`[builder] registerEditor("${type}"): plugin must be an object`)
    return
  }
  if (!plugin.Editor && !(plugin.mode === 'inline' && plugin.Cell)) {
    console.warn(`[builder] registerEditor("${type}"): needs an Editor, or a Cell with mode 'inline'`)
    return
  }
  EDITORS.set(type, { type, mode: 'modal', ...plugin })
}

export const getEditor = (type) => EDITORS.get(type)

export const listEditors = () => Array.from(EDITORS.entries())

export const hasEditor = (type) => EDITORS.has(type)
